/**
 * useBackgroundAudio — keeps audio playing when mobile browser is backgrounded.
 *
 * Uses:
 * 1. navigator.mediaSession to show lock-screen controls
 * 2. A silent audio loop fallback to prevent browser audio suspension
 * 3. Screen Wake Lock API to prevent device sleep (if available)
 */
import { useEffect, useRef, useCallback } from 'react';

// Tiny 1-second silent MP3 as base64 (prevents browser from suspending audio context)
const SILENT_MP3 = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRBqJAAAAAAAAAAAAAAAAAAAA//tQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//tQZB8P8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==';

export default function useBackgroundAudio({ title, artist, isActive }) {
  const silentRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Setup silent audio loop to keep audio context alive on mobile
  useEffect(() => {
    if (!isActive) return;

    const audio = new Audio(SILENT_MP3);
    audio.loop = true;
    audio.volume = 0.01; // nearly silent
    audio.setAttribute('playsinline', '');
    // Attempt to play (may need user gesture)
    audio.play().catch(() => {});
    silentRef.current = audio;

    return () => {
      audio.pause();
      audio.src = '';
      silentRef.current = null;
    };
  }, [isActive]);

  // Setup Media Session for lock screen controls
  useEffect(() => {
    if (!isActive || !('mediaSession' in navigator)) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: title || '루나문',
      artist: artist || '루나문 라이브',
      album: '루나문',
    });

    navigator.mediaSession.playbackState = 'playing';

    return () => {
      navigator.mediaSession.playbackState = 'none';
      navigator.mediaSession.metadata = null;
    };
  }, [isActive, title, artist]);

  // Request Wake Lock to prevent device sleep
  useEffect(() => {
    if (!isActive || !('wakeLock' in navigator)) return;
    let released = false;

    const requestWakeLock = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => { released = true; });
      } catch {}
    };

    requestWakeLock();

    // Re-acquire on visibility change (e.g., returning to tab)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && released && isActive) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [isActive]);

  // Provide a function to set media session action handlers
  const setMediaHandlers = useCallback(({ onPause, onPlay }) => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler('pause', onPause || null);
      navigator.mediaSession.setActionHandler('play', onPlay || null);
    } catch {}
  }, []);

  return { setMediaHandlers };
}
